import type { ConversationMediaAttachmentView } from "@/api/autoparts";
import { Download, FileImage } from "@/components/icons";

function formatShopAttachmentSize(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function shopAttachmentDownloadUrl(value: string) {
  const separator = value.includes("?") ? "&" : "?";
  return `${value}${separator}download=1`;
}

export function ShopMessageAttachment({ attachment }: { attachment: ConversationMediaAttachmentView }) {
  const contentType = String(attachment.contentType || "").toLowerCase();
  const mediaType = String(attachment.mediaType || "").toLowerCase();
  const isVideo = mediaType === "video" || contentType.startsWith("video/");
  const isImage = mediaType === "image" || contentType.startsWith("image/");
  const isAudio = mediaType === "audio" || contentType.startsWith("audio/");

  if (isVideo) {
    return (
      <div className="mt-2 overflow-hidden rounded-xl border border-black/10 bg-black dark:border-white/10">
        <video src={attachment.url} controls playsInline preload="metadata" className="max-h-80 w-full bg-black" />
        <a href={attachment.url} download={attachment.fileName} className="flex items-center justify-between gap-3 bg-card px-3 py-2 text-xs text-card-foreground">
          <span className="min-w-0">
            <span className="block truncate font-medium">{attachment.fileName}</span>
            <span className="block text-[11px] text-muted-foreground">{formatShopAttachmentSize(attachment.size)} · seekable video</span>
          </span>
          <Download className="h-3.5 w-3.5 shrink-0" />
        </a>
      </div>
    );
  }
  if (isImage) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="mt-2 block overflow-hidden rounded-xl border bg-muted/20">
        <img src={attachment.url} alt={attachment.fileName} loading="lazy" className="max-h-72 w-full object-contain" />
        <span className="flex items-center justify-between gap-3 border-t bg-card px-3 py-2 text-xs text-card-foreground">
          <span className="truncate font-medium">{attachment.fileName}</span><Download className="h-3.5 w-3.5 shrink-0" />
        </span>
      </a>
    );
  }
  if (isAudio) {
    return <div className="mt-2 rounded-xl border bg-background/70 p-3"><div className="mb-2 flex items-center justify-between gap-2"><p className="truncate text-xs font-medium">{attachment.fileName}</p><a href={attachment.url} download={attachment.fileName} aria-label={`Download ${attachment.fileName}`} className="shrink-0"><Download className="h-4 w-4" /></a></div><audio src={attachment.url} controls preload="metadata" className="h-9 w-full" /></div>;
  }
  return (
    <a href={attachment.url} download={attachment.fileName} className="mt-2 flex items-center gap-3 rounded-xl border bg-background/70 p-3 text-sm">
      <FileImage className="h-5 w-5 shrink-0" /><span className="min-w-0 flex-1 truncate font-medium">{attachment.fileName}</span><Download className="h-4 w-4 shrink-0" />
    </a>
  );
}

